/* eslint-disable arrow-body-style */
"use client";

import type { FC } from "react";
import { Flex, FormRow, Grid, NumberField, Spacing, Text } from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import { useMaximumMortgageWizard } from "../../../hooks/useMaximumMortgageWizard";
import { BaseSlide, type BaseSlideProps } from "./BaseSlide";
import { OptionalFormRow } from "./OptionalFormRow";
import {
  FormInputDisplayConfiguration,
  InputConfiguration,
} from "@huub-web/ui/services/inputs";
import {
  slideHeadingDescriptionStyle,
  twoColumnGridStyle,
} from "./BaseSlide.css";

type FinancialObligationsSlideProps = BaseSlideProps;

/**
 * Financial obligations slide component for the mortgage wizard
 * Handles monthly financial obligations like loans, student debt, and private lease
 *
 * ⚠️ When adding/removing conditional fields, update filterMaximumMortgageWizardValues.ts
 */
export const FinancialObligationsSlide: FC<
  FinancialObligationsSlideProps
> = () => {
  const { t } = useTranslation();
  const { state, updateFormValues } = useMaximumMortgageWizard();
  const { formValues } = state;
  const plurality = formValues.hasPartner ? "plural" : "singular";
  const isRenovate = formValues.purpose === "renovate";

  return (
    <BaseSlide
      slideKey="financialObligations"
      title={t(`maxMortgageWizard.financialObligations.title.${plurality}`)}
      description={t("maxMortgageWizard.financialObligations.description")}
    >
      <OptionalFormRow
        id="wizard__hasLoans"
        data-field-key="hasLoans"
        label={t("maxMortgageWizard.financialObligations.loans.label")}
        checked={formValues.hasLoans ?? false}
        onChange={(value) => updateFormValues({ hasLoans: value })}
      >
        <Text markdown variant="bodyS">
          {t(
            `maxMortgageWizard.financialObligations.loans.description.${plurality}`,
          )}
        </Text>

        <Spacing height="xs" />

        <Grid variant="none" className={twoColumnGridStyle}>
          <FormRow id="wizard__loans" data-field-key="loans">
            <NumberField
              {...InputConfiguration.money.props}
              leading={FormInputDisplayConfiguration.money.leading}
              value={
                formValues.loans && formValues.loans !== ""
                  ? Number(formValues.loans)
                  : undefined
              }
              onChange={(value) =>
                updateFormValues({ loans: value?.toString() ?? "" })
              }
              validation={["required"]}
              name={t("maxMortgageWizard.financialObligations.loans.name")}
              label={t(
                "maxMortgageWizard.financialObligations.loans.fieldLabel",
              )}
            />
          </FormRow>
          {formValues.hasPartner && (
            <FormRow id="wizard__loansPartner" data-field-key="loansPartner">
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  formValues.loansPartner && formValues.loansPartner !== ""
                    ? Number(formValues.loansPartner)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({ loansPartner: value?.toString() ?? "" })
                }
                name={t(
                  "maxMortgageWizard.financialObligations.loans.partnerName",
                )}
                label={t(
                  "maxMortgageWizard.financialObligations.loans.partnerFieldLabel",
                )}
              />
            </FormRow>
          )}
        </Grid>
      </OptionalFormRow>

      <OptionalFormRow
        id="wizard__hasStudentDebt"
        data-field-key="hasStudentDebt"
        label={t("maxMortgageWizard.financialObligations.studentDebt.label")}
        checked={formValues.hasStudentDebt ?? false}
        onChange={(value) => updateFormValues({ hasStudentDebt: value })}
      >
        <Text markdown variant="bodyS">
          {t(
            `maxMortgageWizard.financialObligations.studentDebt.description.${plurality}`,
          )}
        </Text>
        <Spacing height="xs" />
        <Grid variant="none" className={twoColumnGridStyle}>
          <FormRow id="wizard__studentDebt" data-field-key="studentDebt">
            <NumberField
              {...InputConfiguration.money.props}
              leading={FormInputDisplayConfiguration.money.leading}
              value={
                formValues.studentDebt && formValues.studentDebt !== ""
                  ? Number(formValues.studentDebt)
                  : undefined
              }
              onChange={(value) =>
                updateFormValues({ studentDebt: value?.toString() ?? "" })
              }
              validation={["required"]}
              name={t(
                "maxMortgageWizard.financialObligations.studentDebt.name",
              )}
              label={t(
                "maxMortgageWizard.financialObligations.studentDebt.fieldLabel",
              )}
            />
          </FormRow>
          {formValues.hasPartner && (
            <FormRow
              id="wizard__studentDebtPartner"
              data-field-key="studentDebtPartner"
            >
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  formValues.studentDebtPartner &&
                  formValues.studentDebtPartner !== ""
                    ? Number(formValues.studentDebtPartner)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({
                    studentDebtPartner: value?.toString() ?? "",
                  })
                }
                name={t(
                  "maxMortgageWizard.financialObligations.studentDebt.partnerName",
                )}
                label={t(
                  "maxMortgageWizard.financialObligations.studentDebt.partnerFieldLabel",
                )}
              />
            </FormRow>
          )}
        </Grid>
      </OptionalFormRow>

      <OptionalFormRow
        id="wizard__hasPrivateLease"
        data-field-key="hasPrivateLease"
        label={t("maxMortgageWizard.financialObligations.privateLease.label")}
        checked={formValues.hasPrivateLease ?? false}
        onChange={(value) => updateFormValues({ hasPrivateLease: value })}
      >
        <Text markdown variant="bodyS">
          {t(
            `maxMortgageWizard.financialObligations.privateLease.description.${plurality}`,
          )}
        </Text>
        <Spacing height="xs" />
        <Grid variant="none" className={twoColumnGridStyle}>
          <FormRow id="wizard__privateLease" data-field-key="privateLease">
            <NumberField
              {...InputConfiguration.money.props}
              leading={FormInputDisplayConfiguration.money.leading}
              value={
                formValues.privateLease && formValues.privateLease !== ""
                  ? Number(formValues.privateLease)
                  : undefined
              }
              onChange={(value) =>
                updateFormValues({ privateLease: value?.toString() ?? "" })
              }
              validation={["required"]}
              name={t(
                "maxMortgageWizard.financialObligations.privateLease.name",
              )}
              label={t(
                "maxMortgageWizard.financialObligations.privateLease.fieldLabel",
              )}
            />
          </FormRow>
          {formValues.hasPartner && (
            <FormRow
              id="wizard__privateLeasePartner"
              data-field-key="privateLeasePartner"
            >
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  formValues.privateLeasePartner &&
                  formValues.privateLeasePartner !== ""
                    ? Number(formValues.privateLeasePartner)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({
                    privateLeasePartner: value?.toString() ?? "",
                  })
                }
                name={t(
                  "maxMortgageWizard.financialObligations.privateLease.partnerName",
                )}
                label={t(
                  "maxMortgageWizard.financialObligations.privateLease.partnerFieldLabel",
                )}
              />
            </FormRow>
          )}
        </Grid>
      </OptionalFormRow>

      {!isRenovate && (
        <Flex direction="column" gap="m">
          <Spacing />
          <Flex direction="column" gap="s">
            <Text as="h3" variant="displayS" bold>
              {t(
                `maxMortgageWizard.financialObligations.ownFunds.title.${plurality}`,
              )}
            </Text>
            <Text markdown className={slideHeadingDescriptionStyle}>
              {t(
                `maxMortgageWizard.financialObligations.ownFunds.description.${plurality}`,
              )}
            </Text>
          </Flex>
          <Grid variant="none" className={twoColumnGridStyle}>
            <FormRow id="wizard__ownCapital" data-field-key="ownCapital">
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  formValues.ownCapital && formValues.ownCapital !== ""
                    ? Number(formValues.ownCapital)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({ ownCapital: value?.toString() ?? "" })
                }
                name={t("maxMortgageWizard.financialObligations.ownFunds.name")}
                label={t(
                  "maxMortgageWizard.financialObligations.ownFunds.fieldLabel",
                )}
              />
            </FormRow>
          </Grid>
        </Flex>
      )}
    </BaseSlide>
  );
};
